import { useTranslation } from 'react-i18next'
import { Card } from './Card'

/* ─── Role access matrix (MS-741 AC4) ───
   Which Studio surfaces each role may open, surfaced above the Studio Access
   user list so an admin sees what a role unlocks. Collapsed by default
   because the table is long. Rows ported verbatim from the prototype's
   `renderRoleMatrix()` (agent-studio.html:7075-7097) — subpages are collapsed
   to "(All surfaces)" where a section's surfaces share the same access;
   expanded (name › subpage) only where they differ. */

type RoleMatrixLabelKey =
  | 'globalSettings.roleMatrix.rows.home'
  | 'globalSettings.roleMatrix.rows.notifications'
  | 'globalSettings.roleMatrix.rows.agents'
  | 'globalSettings.roleMatrix.rows.agentDnaSkillsKnowledge'
  | 'globalSettings.roleMatrix.rows.agentConnectionsGuardrailsAccess'
  | 'globalSettings.roleMatrix.rows.mcp'
  | 'globalSettings.roleMatrix.rows.globalSkillsKnowledge'
  | 'globalSettings.roleMatrix.rows.globalConnectionsGuardrailsAccess'
  | 'globalSettings.roleMatrix.rows.marketplace'
  | 'globalSettings.roleMatrix.rows.workflows'
  | 'globalSettings.roleMatrix.rows.auditLog'
  | 'globalSettings.roleMatrix.rows.agentsAnalytics'
  | 'globalSettings.roleMatrix.rows.mcpAnalytics'

interface RoleMatrixRow {
  labelKey: RoleMatrixLabelKey
  admin: boolean
  editor: boolean
}

const ROWS: RoleMatrixRow[] = [
  { labelKey: 'globalSettings.roleMatrix.rows.home', admin: true, editor: true },
  { labelKey: 'globalSettings.roleMatrix.rows.notifications', admin: true, editor: false },
  { labelKey: 'globalSettings.roleMatrix.rows.agents', admin: true, editor: true },
  { labelKey: 'globalSettings.roleMatrix.rows.agentDnaSkillsKnowledge', admin: true, editor: true },
  { labelKey: 'globalSettings.roleMatrix.rows.agentConnectionsGuardrailsAccess', admin: true, editor: false },
  { labelKey: 'globalSettings.roleMatrix.rows.mcp', admin: true, editor: false },
  { labelKey: 'globalSettings.roleMatrix.rows.globalSkillsKnowledge', admin: true, editor: true },
  { labelKey: 'globalSettings.roleMatrix.rows.globalConnectionsGuardrailsAccess', admin: true, editor: false },
  { labelKey: 'globalSettings.roleMatrix.rows.marketplace', admin: true, editor: true },
  { labelKey: 'globalSettings.roleMatrix.rows.workflows', admin: true, editor: true },
  { labelKey: 'globalSettings.roleMatrix.rows.auditLog', admin: true, editor: false },
  { labelKey: 'globalSettings.roleMatrix.rows.agentsAnalytics', admin: true, editor: true },
  { labelKey: 'globalSettings.roleMatrix.rows.mcpAnalytics', admin: true, editor: false },
]

function AccessIcon({ access, label }: Readonly<{ access: boolean; label: string }>) {
  return (
    // Tailwind's preflight sets `svg { display: block }`, which takes the icon out of
    // inline flow — the parent td's `text-align: center` only affects inline content,
    // so without this override the icon sits flush-left instead of centered under its
    // column header.
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke={access ? 'var(--accent)' : 'var(--text-tertiary)'} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-label={label} style={{ display: 'inline-block' }}>
      {access && <polyline points="20 6 9 17 4 12" />}
      {!access && <line x1="18" y1="6" x2="6" y2="18" />}
      {!access && <line x1="6" y1="6" x2="18" y2="18" />}
    </svg>
  )
}

function AccessCell({ access, accessLabel, noAccessLabel }: Readonly<{ access: boolean; accessLabel: string; noAccessLabel: string }>) {
  return (
    <td style={{ textAlign: 'center' }}>
      <AccessIcon access={access} label={access ? accessLabel : noAccessLabel} />
    </td>
  )
}

export function RoleMatrixCard() {
  const { t } = useTranslation()
  const accessLabel = t('globalSettings.roleMatrix.access')
  const noAccessLabel = t('globalSettings.roleMatrix.noAccess')

  return (
    <Card
      title={t('globalSettings.roleMatrix.title')}
      subtitle={t('globalSettings.roleMatrix.subtitle')}
      defaultCollapsed
      className="mb-16"
    >
      <div style={{ overflowX: 'auto' }}>
        <table className="data-table" id="role-matrix-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>{t('globalSettings.roleMatrix.columns.surface')}</th>
              <th style={{ textAlign: 'center', width: 120 }}>{t('globalSettings.roleMatrix.columns.admin')}</th>
              <th style={{ textAlign: 'center', width: 120 }}>{t('globalSettings.roleMatrix.columns.editor')}</th>
            </tr>
          </thead>
          <tbody id="role-matrix-body">
            {ROWS.map(row => (
              <tr key={row.labelKey}>
                <td>{t(row.labelKey)}</td>
                <AccessCell access={row.admin} accessLabel={accessLabel} noAccessLabel={noAccessLabel} />
                <AccessCell access={row.editor} accessLabel={accessLabel} noAccessLabel={noAccessLabel} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  )
}
